import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config/api';
import AdminLayout from '../components/AdminLayout';
import KnowledgeTree from '../components/KnowledgeTree';

const Container = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const SectionTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--gray-900);
`;

// 左侧目录树 + 右侧知识列表
const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 1rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MoveBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  background: #f8fafc;
  border: 1px solid var(--gray-200, #e5e7eb);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--gray-300, #d1d5db);
  border-radius: 6px;
  font-size: 0.85rem;
  background: #fff;
`;

const AdminNav = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const AdminNavLink = styled(Link)`
  padding: 0.5rem 1rem;
  background-color: var(--gray-100);
  color: var(--gray-600);
  border-radius: var(--border-radius-sm);
  font-weight: 500;
  text-decoration: none;
  transition: all var(--transition-fast) ease-in-out;
  font-size: 0.875rem;
  
  &:hover {
    background-color: var(--gray-200);
  }
  
  &.active {
    background-color: var(--primary-color);
    color: white;
  }
`;

const QuickLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const QuickLink = styled.button`
  padding: 0.75rem 1.25rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #f8f9fa;
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
`;

const SearchRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Card = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--gray-800);
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast) ease-in-out;
  
  &.primary {
    background-color: var(--primary-color);
    color: white;
    
    &:hover {
      background-color: var(--primary-dark);
    }
  }
  
  &.danger {
    background-color: #dc2626;
    color: white;
    
    &:hover {
      background-color: #b91c1c;
    }
  }
  
  &.secondary {
    background-color: var(--gray-200);
    color: var(--gray-700);
    
    &:hover {
      background-color: var(--gray-300);
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--gray-700);
  background-color: var(--gray-50);
  border-bottom: 1px solid var(--gray-200);
  
  &:first-child {
    border-radius: var(--border-radius-lg) 0 0 0;
  }
  
  &:last-child {
    border-radius: 0 var(--border-radius-lg) 0 0;
  }
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--gray-100);
  
  &:last-child {
    white-space: nowrap;
  }
`;

const Tr = styled.tr`
  &:hover {
    background-color: var(--gray-50);
  }
  
  &.empty {
    text-align: center;
    color: var(--gray-500);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background-color: white;
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: var(--gray-900);
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--gray-700);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  transition: border-color var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: border-color var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const ModalContent = styled.div`
  padding: 1rem 0;
`;

const Toast = styled.div`
  position: fixed;
  top: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  border-radius: var(--border-radius-md);
  color: white;
  font-weight: 500;
  z-index: 1100;
  animation: slideIn 0.3s ease-out;
  
  &.success {
    background-color: #10b981;
  }
  
  &.error {
    background-color: #ef4444;
  }
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  flex: 1;
  max-width: 400px;
  padding: 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  
  &.active {
    background-color: #dcfce7;
    color: #16a34a;
  }
  
  &.inactive {
    background-color: #fef2f2;
    color: #dc2626;
  }
`;

const AdminKnowledge = () => {
  const navigate = useNavigate();
  const [knowledgeList, setKnowledgeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: '',
    keywords: ''
  });
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [testQuery, setTestQuery] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [relearning, setRelearning] = useState(false);
  // 后台学习任务进度（update / relearn 都是长任务，接口只负责启动）
  const [learnTask, setLearnTask] = useState(null);
  const learning = learnTask && learnTask.running;
  const [showUrlLearnModal, setShowUrlLearnModal] = useState(false);
  const [urlLearnForm, setUrlLearnForm] = useState({ url: '', tags: '', category: '' });
  const [urlLearning, setUrlLearning] = useState(false);
  const [urlLearnResult, setUrlLearnResult] = useState(null);
  const [showAutoLearnModal, setShowAutoLearnModal] = useState(false);
  const [autoLearnForm, setAutoLearnForm] = useState({ keywords: '', restrictions: '', maxResults: '10', tags: '', category: '' });
  const [autoLearning, setAutoLearning] = useState(false);
  const [autoLearnResult, setAutoLearnResult] = useState(null);
  const [showGraphModal, setShowGraphModal] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  // 目录树
  const [categoryTree, setCategoryTree] = useState([]);
  const [unclassifiedCount, setUnclassifiedCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedNodes, setExpandedNodes] = useState({});
  const [checkedIds, setCheckedIds] = useState([]);
  const [moveTarget, setMoveTarget] = useState('');

  const fetchKnowledge = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/knowledge');
      setKnowledgeList(response.data.knowledgeBase || []);
    } catch (error) {
      console.error('获取知识库失败:', error);
      showToast('获取知识库失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledge();
    fetchCategories();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ===== 目录树 =====

  const fetchCategories = async () => {
    try {
      const res = await axios.get('/api/knowledge/categories');
      const data = res.data.data || {};
      setCategoryTree(data.tree || []);
      setUnclassifiedCount(data.unclassified || 0);
    } catch (error) {
      console.error('获取知识目录失败:', error);
    }
  };

  const handleCreateCategory = async (parentId) => {
    const name = window.prompt(parentId ? '请输入子目录名称' : '请输入根目录名称');
    if (!name || !name.trim()) return;
    try {
      const res = await axios.post('/api/knowledge/categories', {
        name: name.trim(),
        parentId: parentId || null
      });
      showToast(res.data.message || '目录已创建');
      if (parentId) setExpandedNodes(prev => ({ ...prev, [parentId]: true }));
      fetchCategories();
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.message) || error.message;
      showToast('创建失败：' + msg, 'error');
    }
  };

  const handleRenameCategory = async (cat) => {
    const name = window.prompt('重命名目录', cat.name);
    if (!name || !name.trim() || name.trim() === cat.name) return;
    try {
      const res = await axios.put(`/api/knowledge/categories/${cat.id}`, { name: name.trim() });
      showToast(res.data.message || '已重命名');
      fetchCategories();
      fetchKnowledge();
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.message) || error.message;
      showToast('重命名失败：' + msg, 'error');
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(
      `确定删除目录「${cat.name}」？\n\n该目录下 ${cat.totalCount || 0} 条知识会移动到「未分类」，知识本身不会被删除。`
    )) return;
    try {
      const res = await axios.delete(`/api/knowledge/categories/${cat.id}`);
      showToast(res.data.message || '目录已删除');
      setSelectedCategory('all');
      fetchCategories();
      fetchKnowledge();
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.message) || error.message;
      showToast('删除失败：' + msg, 'error');
    }
  };

  const toggleNode = (id) => {
    setExpandedNodes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // 目录下拉用：把树摊平并带层级缩进
  const flatCategories = () => {
    const out = [];
    const walk = (nodes, depth) => (nodes || []).forEach(n => {
      out.push({ id: n.id, name: n.name, depth });
      walk(n.children, depth + 1);
    });
    walk(categoryTree, 0);
    return out;
  };

  // 选中目录时同时展示它所有子目录里的知识
  const collectCategoryIds = (targetId) => {
    const ids = [];
    const walk = (list, collecting) => (list || []).forEach(n => {
      if (collecting || String(n.id) === String(targetId)) {
        ids.push(String(n.id));
        walk(n.children, true);
      } else {
        walk(n.children, false);
      }
    });
    walk(categoryTree, false);
    return ids;
  };

  const handleMoveChecked = async () => {
    if (checkedIds.length === 0) {
      showToast('请先勾选要移动的知识', 'error');
      return;
    }
    const target = moveTarget === '' ? null : moveTarget;
    try {
      const res = await axios.post('/api/knowledge/move', { ids: checkedIds, categoryId: target });
      showToast(res.data.message || '已移动');
      setCheckedIds([]);
      setMoveTarget('');
      fetchKnowledge();
      fetchCategories();
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.message) || error.message;
      showToast('移动失败：' + msg, 'error');
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      question: '',
      answer: '',
      category: '',
      categoryId: (selectedCategory === 'all' || selectedCategory === 'none') ? '' : selectedCategory,
      keywords: ''
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      question: item.question || '',
      answer: item.answer || '',
      category: item.category || '',
      categoryId: item.categoryId || '',
      keywords: item.keywords ? item.keywords.join(', ') : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这条知识吗？')) return;
    
    try {
      await axios.delete(`/api/knowledge/${id}`);
      showToast('删除成功');
      fetchKnowledge();
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.message) || error.message;
      console.error('删除失败:', msg, error);
      showToast('删除失败：' + msg, 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k)
      };
      
      if (editingItem) {
        await axios.put(`/api/knowledge/${editingItem.id}`, data);
        showToast('更新成功');
      } else {
        await axios.post('/api/knowledge', data);
        showToast('添加成功');
      }
      
      setShowModal(false);
      fetchKnowledge();
    } catch (error) {
      // 带上后端原因（如「请填写答案」「IP地址不在白名单中」），否则只知道失败不知为什么
      const msg = (error.response && error.response.data && error.response.data.message) || error.message;
      console.error('保存失败:', msg, error);
      showToast('保存失败：' + msg, 'error');
    }
  };

  const handleLearnUrl = () => {
    setUrlLearnForm({ url: '', tags: '', category: '' });
    setUrlLearnResult(null);
    setShowUrlLearnModal(true);
  };

  const handleAutoLearn = () => {
    setAutoLearnForm({ keywords: '', restrictions: '', maxResults: '10', tags: '', category: '' });
    setAutoLearnResult(null);
    setShowAutoLearnModal(true);
  };

  const handleAutoLearnSubmit = async () => {
    if (!autoLearnForm.keywords.trim()) {
      showToast('请输入搜索关键词', 'error');
      return;
    }

    const keywords = autoLearnForm.keywords.split(',').map(k => k.trim()).filter(k => k);
    const restrictions = autoLearnForm.restrictions.split(',').map(r => r.trim()).filter(r => r);
    const tags = autoLearnForm.tags.split(',').map(t => t.trim()).filter(t => t);

    if (keywords.length === 0) {
      showToast('请输入至少一个搜索关键词', 'error');
      return;
    }

    setAutoLearning(true);
    setAutoLearnResult(null);

    try {
      const response = await axios.post(API.KNOWLEDGE.AUTO_LEARN, {
        keywords,
        restrictions,
        maxResults: parseInt(autoLearnForm.maxResults) || 10,
        tags,
        category: autoLearnForm.category
      });

      setAutoLearnResult({
        success: response.data.success,
        message: response.data.message,
        data: response.data.data
      });

      if (response.data.success) {
        fetchKnowledge();
      }
    } catch (error) {
      console.error('自动学习失败:', error);
      setAutoLearnResult({
        success: false,
        message: error.response?.data?.message || '自动学习失败'
      });
    } finally {
      setAutoLearning(false);
    }
  };

  const fetchGraphData = async () => {
    try {
      setGraphLoading(true);
      const [graphRes, analysisRes] = await Promise.all([
        axios.get(API.KNOWLEDGE.GRAPH),
        axios.get(API.KNOWLEDGE.ANALYSIS)
      ]);
      setGraphData(graphRes.data.data);
      setAnalysisData(analysisRes.data.data);
    } catch (error) {
      console.error('获取知识图谱失败:', error);
      showToast('获取知识图谱失败', 'error');
    } finally {
      setGraphLoading(false);
    }
  };

  const openGraphModal = () => {
    fetchGraphData();
    setShowGraphModal(true);
  };

  const openMaintenanceModal = () => {
    setShowMaintenanceModal(true);
  };

  const handleClearOldKnowledge = async () => {
    if (!window.confirm('确定要清理30天前的旧知识吗？此操作不可撤销。')) {
      return;
    }
    try {
      const response = await axios.delete(`${API.KNOWLEDGE.LIST}/clear`);
      if (response.data.success) {
        showToast(`已清理 ${response.data.data.deleted} 条旧知识`, 'success');
        fetchKnowledge();
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (error) {
      showToast('清理失败', 'error');
    }
  };

  const handleClearAllKnowledge = async () => {
    if (!window.confirm('确定要清空所有知识库吗？此操作不可撤销！')) {
      return;
    }
    try {
      const response = await axios.delete(`${API.KNOWLEDGE.LIST}/all`);
      if (response.data.success) {
        showToast('已清空所有知识', 'success');
        fetchKnowledge();
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (error) {
      showToast('清空失败', 'error');
    }
  };

  const handleExportKnowledge = async () => {
    try {
      const response = await axios.get(`${API.KNOWLEDGE.LIST}/export`);
      if (response.data.success) {
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `knowledge-base-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast('导出成功', 'success');
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (error) {
      showToast('导出失败', 'error');
    }
  };

  const handleUrlLearnSubmit = async () => {
    if (!urlLearnForm.url.trim()) {
      showToast('请输入网址', 'error');
      return;
    }
    
    if (!urlLearnForm.tags.trim()) {
      showToast('请输入至少一个标签', 'error');
      return;
    }
    
    setUrlLearning(true);
    setUrlLearnResult(null);
    
    const tags = urlLearnForm.tags.split(',').map(t => t.trim()).filter(t => t);
    
    try {
      const response = await axios.post('/api/knowledge/learn', { 
        type: 'url', 
        source: urlLearnForm.url,
        metadata: {
          tags: tags,
          category: urlLearnForm.category
        }
      });
      
      const result = response.data.data;
      
      if (result && result.skipped) {
        setUrlLearnResult({
          success: false,
          skipped: true,
          message: `内容与指定标签不相关，已跳过学习 (相关度: ${result.relevanceScore}%)`,
          relevanceScore: result.relevanceScore,
          originalLength: result.originalContentLength,
          filteredLength: result.filteredContentLength
        });
      } else {
        setUrlLearnResult({
          success: true,
          message: `学习成功！原始内容 ${result.originalContentLength} 字符，学习内容 ${result.learnedContentLength} 字符 (保留率: ${((result.learnedContentLength / result.originalContentLength) * 100).toFixed(1)}%)`,
          title: result?.title || urlLearnForm.url,
          tags: tags,
          originalLength: result.originalContentLength,
          learnedLength: result.learnedContentLength
        });
        fetchKnowledge();
      }
    } catch (error) {
      console.error('学习失败:', error);
      setUrlLearnResult({
        success: false,
        message: error.response?.data?.message || '学习失败'
      });
    } finally {
      setUrlLearning(false);
    }
  };

  const handleTestKnowledge = async () => {
    if (!testQuery.trim()) {
      showToast('请输入测试问题', 'error');
      return;
    }
    
    setTestLoading(true);
    setTestResult(null);
    
    try {
      const response = await axios.post('/api/knowledge/deepseek/chat', {
        message: testQuery
      });
      setTestResult(response.data);
    } catch (error) {
      console.error('测试失败:', error);
      showToast('测试失败: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setTestLoading(false);
    }
  };

  // 轮询后台学习任务进度，结束后刷新知识列表
  const pollLearnTask = () => {
    const timer = setInterval(async () => {
      try {
        const response = await axios.get('/api/knowledge/learn-status');
        const task = response.data && response.data.data;
        if (!task) {
          clearInterval(timer);
          setRelearning(false);
          return;
        }
        setLearnTask(task);
        if (!task.running) {
          clearInterval(timer);
          setRelearning(false);
          if (task.error) {
            showToast('学习失败: ' + task.error, 'error');
          } else {
            showToast(`学习完成：新增 ${task.ok} 条，更新 ${task.skipped || 0} 条，失败 ${task.fail} 条`, 'success');
          }
          fetchKnowledge();
        }
      } catch (error) {
        clearInterval(timer);
        setRelearning(false);
        console.error('获取学习进度失败:', error);
      }
    }, 3000);
  };

  const handleRelearn = async () => {
    if (!window.confirm('确定要重新学习整个网站吗？全站抓取 + AI 提炼，通常需要几分钟，任务将在后台执行。')) return;

    setRelearning(true);
    showToast('已开始重新学习，正在后台执行...');

    try {
      const response = await axios.post('/api/knowledge/relearn');
      if (!response.data.success) {
        setRelearning(false);
        showToast(response.data.message || '启动失败', 'error');
        return;
      }
      pollLearnTask();
    } catch (error) {
      console.error('启动重新学习失败:', error);
      setRelearning(false);
      showToast('启动失败: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const handleUpdateKnowledge = async () => {
    setRelearning(true);
    showToast('已开始更新知识库，正在后台执行...');

    try {
      const response = await axios.post('/api/knowledge/update');
      if (!response.data.success) {
        setRelearning(false);
        showToast(response.data.message || '启动失败', 'error');
        return;
      }
      pollLearnTask();
    } catch (error) {
      console.error('启动更新失败:', error);
      setRelearning(false);
      showToast('启动失败: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(filteredList, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportName = `knowledge_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportName);
    linkElement.click();
  };

  // 选中目录时，同时展示其所有子目录里的知识
  const categoryScope = (selectedCategory === 'all' || selectedCategory === 'none')
    ? null
    : collectCategoryIds(selectedCategory);

  const filteredList = knowledgeList.filter(item => {
    if (selectedCategory === 'none') {
      if (item.categoryId !== null && item.categoryId !== undefined && item.categoryId !== '') return false;
    } else if (categoryScope) {
      if (!categoryScope.includes(String(item.categoryId))) return false;
    }

    const kw = searchTerm.toLowerCase();
    return (item.question || '').toLowerCase().includes(kw)
      || (item.answer || '').toLowerCase().includes(kw);
  });

  const allChecked = filteredList.length > 0 && filteredList.every(item => checkedIds.includes(item.id));

  const categoryNameOf = (item) => {
    if (item.categoryId === null || item.categoryId === undefined || item.categoryId === '') {
      return '未分类';
    }
    const cat = flatCategories().find(c => String(c.id) === String(item.categoryId));
    return cat ? cat.name : (item.category || '-');
  };

  const selectedCategoryName = selectedCategory === 'all'
    ? '全部知识'
    : selectedCategory === 'none'
      ? '未分类'
      : (flatCategories().find(c => String(c.id) === String(selectedCategory)) || {}).name || '该目录';

  return (
    <AdminLayout title="知识库管理">
      <Container>
        <SectionTitle>知识库管理</SectionTitle>
        
        <AdminNav>
          <AdminNavLink to="/admin/knowledge" className="active">
            知识库管理
          </AdminNavLink>
        </AdminNav>

        <QuickLinks>
          <QuickLink onClick={() => navigate('/admin/users')}>👥 用户管理</QuickLink>
          <QuickLink onClick={() => navigate('/admin/history')}>💬 客服记录</QuickLink>
          <QuickLink onClick={() => navigate('/admin/settings')}>⚙️ 配置管理</QuickLink>
        </QuickLinks>
        
        <Card>
          <SearchRow>
            <SearchBar>
              <SearchInput
                type="text"
                placeholder="搜索知识..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem', alignSelf: 'center' }}>
                共 {filteredList.length} 条
              </span>
            </SearchBar>
            <Button className="primary" onClick={handleAdd}>
              + 添加知识
            </Button>
          </SearchRow>
          
          <ButtonGroup>
            <Button className="secondary" onClick={() => setShowTestModal(true)} style={{ background: '#8b5cf6', color: 'white' }}>
              🧪 测试知识库
            </Button>
            <Button className="secondary" onClick={openGraphModal} style={{ background: '#10b981', color: 'white' }}>
              🗺️ 知识图谱
            </Button>
            <Button className="secondary" onClick={openMaintenanceModal} style={{ background: '#6366f1', color: 'white' }}>
              ⚙️ 知识库维护
            </Button>
            <Button className="secondary" onClick={handleExport}>
              📥 导出
            </Button>
            <Button className="secondary" onClick={handleUpdateKnowledge} disabled={learning}>
              {learning && learnTask.type === 'update'
                ? `更新中 ${learnTask.done}/${learnTask.total || '?'}`
                : '🔄 更新网站知识'}
            </Button>
            <Button className="secondary" onClick={handleRelearn} disabled={learning}>
              {learning && learnTask.type === 'relearn'
                ? `重新学习中 ${learnTask.done}/${learnTask.total || '?'}`
                : '🌐 重新学习全站'}
            </Button>
            <Button className="secondary" onClick={handleLearnUrl}>
              🏷️ 按标签学习URL
            </Button>
            <Button className="secondary" onClick={handleAutoLearn}>
              🔍 自动搜索学习
            </Button>
          </ButtonGroup>
        </Card>
        
        <TwoColumn>
          <div>
            <KnowledgeTree
              tree={categoryTree}
              unclassified={unclassifiedCount}
              selected={selectedCategory}
              onSelect={(value) => { setSelectedCategory(value); setCheckedIds([]); }}
              expanded={expandedNodes}
              onToggle={toggleNode}
              onCreate={handleCreateCategory}
              onRename={handleRenameCategory}
              onDelete={handleDeleteCategory}
            />
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle>
                  知识列表
                  <span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--gray-500)', marginLeft: '0.5rem' }}>
                    （{selectedCategoryName}，{filteredList.length} 条）
                  </span>
                </CardTitle>
              </CardHeader>

              {!loading && filteredList.length > 0 && (
                <div style={{ padding: '0.75rem 1rem 0' }}>
                  <MoveBar>
                    <span>已选 {checkedIds.length} 条</span>
                    <Select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)}>
                      <option value="">移到「未分类」</option>
                      {flatCategories().map(c => (
                        <option key={c.id} value={c.id}>{'　'.repeat(c.depth) + c.name}</option>
                      ))}
                    </Select>
                    <Button
                      className="primary"
                      onClick={handleMoveChecked}
                      style={{ padding: '0.3rem 0.75rem', fontSize: '0.85rem' }}
                    >
                      移动到该目录
                    </Button>
                    {checkedIds.length > 0 && (
                      <Button
                        className="secondary"
                        onClick={() => setCheckedIds([])}
                        style={{ padding: '0.3rem 0.75rem', fontSize: '0.85rem' }}
                      >
                        取消选择
                      </Button>
                    )}
                  </MoveBar>
                </div>
              )}

              {loading ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                  加载中...
                </div>
              ) : filteredList.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray-400)' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
                  <p>{searchTerm ? '没有匹配的知识' : '该目录下暂无知识'}</p>
                  <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>点击上方「+ 添加知识」按钮开始添加</p>
                </div>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={allChecked}
                          onChange={(e) => setCheckedIds(e.target.checked ? filteredList.map(i => i.id) : [])}
                        />
                      </Th>
                      <Th>问题</Th>
                      <Th>所在目录</Th>
                      <Th>关键词</Th>
                      <Th>状态</Th>
                      <Th>操作</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.map(item => (
                      <Tr key={item.id}>
                        <Td>
                          <input
                            type="checkbox"
                            checked={checkedIds.includes(item.id)}
                            onChange={(e) => setCheckedIds(prev =>
                              e.target.checked ? [...prev, item.id] : prev.filter(x => x !== item.id)
                            )}
                          />
                        </Td>
                        <Td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.question}>
                          {item.question}
                        </Td>
                        <Td>{categoryNameOf(item)}</Td>
                        <Td>{item.keywords?.join(', ') || '-'}</Td>
                        <Td>
                          <StatusBadge className={item.active !== false ? 'active' : 'inactive'}>
                            {item.active !== false ? '启用' : '禁用'}
                          </StatusBadge>
                        </Td>
                        <Td>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button className="secondary" onClick={() => handleEdit(item)} style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}>
                              编辑
                            </Button>
                            <Button className="danger" onClick={() => handleDelete(item.id)} style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}>
                              删除
                            </Button>
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </div>
        </TwoColumn>
        
        {showModal && (
          <ModalOverlay onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
            }
          }}>
            <Modal onClick={(e) => e.stopPropagation()}>
              <ModalTitle>{editingItem ? '编辑知识' : '添加知识'}</ModalTitle>
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>问题 *</Label>
                  <Input
                    type="text"
                    required
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    placeholder="输入问题"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>答案 *</Label>
                  <Textarea
                    required
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    placeholder="输入答案"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>所属目录</Label>
                  <Select
                    value={formData.categoryId || ''}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                    style={{ width: '100%' }}
                  >
                    <option value="">未分类</option>
                    {flatCategories().map(c => (
                      <option key={c.id} value={c.id}>{'　'.repeat(c.depth) + c.name}</option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>分类</Label>
                  <Input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="输入分类（可选，选目录后会自动填为目录名）"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>关键词</Label>
                  <Input
                    type="text"
                    value={formData.keywords}
                    onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                    placeholder="多个关键词用逗号分隔（可选）"
                  />
                </FormGroup>
                <ModalActions>
                  <Button className="secondary" onClick={() => setShowModal(false)}>
                    取消
                  </Button>
                  <Button className="primary" type="submit">
                    {editingItem ? '保存修改' : '添加'}
                  </Button>
                </ModalActions>
              </form>
            </Modal>
          </ModalOverlay>
        )}

        {showTestModal && (
          <ModalOverlay onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowTestModal(false);
              setTestResult(null);
            }
          }}>
            <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
              <ModalTitle>🧪 知识库测试</ModalTitle>
              <p style={{ color: 'var(--gray-600)', marginBottom: '1rem', fontSize: '0.875rem' }}>
                输入问题测试智能客服的回答效果
              </p>
              <FormGroup>
                <Label>测试问题</Label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Input
                    type="text"
                    value={testQuery}
                    onChange={(e) => setTestQuery(e.target.value)}
                    placeholder="例如：航泰公司提供哪些产品？"
                    onKeyPress={(e) => e.key === 'Enter' && handleTestKnowledge()}
                  />
                  <Button 
                    className="primary" 
                    onClick={handleTestKnowledge}
                    disabled={testLoading}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {testLoading ? '回答中...' : '测试'}
                  </Button>
                </div>
              </FormGroup>
              
              {testLoading && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                  <div style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</div>
                  <p>正在生成回答...</p>
                </div>
              )}
              
              {testResult && !testLoading && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ 
                    padding: '1rem', 
                    background: testResult.success ? '#f0fdf4' : '#fef2f2', 
                    borderRadius: '8px',
                    border: `1px solid ${testResult.success ? '#86efac' : '#fca5a5'}`
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: testResult.success ? '#166534' : '#991b1b' }}>
                      AI 回答:
                    </div>
                    <div style={{ 
                      color: '#374151', 
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {testResult.answer}
                    </div>
                    {testResult.sources && testResult.sources.length > 0 && (
                      <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                          参考来源:
                        </div>
                        {testResult.sources.map((source, index) => (
                          <div key={index} style={{ fontSize: '0.875rem', color: '#374151' }}>
                            • {source.title || source}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <ModalActions>
                <Button className="secondary" onClick={() => { setShowTestModal(false); setTestResult(null); }}>
                  关闭
                </Button>
              </ModalActions>
            </Modal>
          </ModalOverlay>
        )}

        {showUrlLearnModal && (
          <ModalOverlay onClick={(e) => {
            if (e.target === e.currentTarget && !urlLearning) {
              setShowUrlLearnModal(false);
            }
          }}>
            <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <ModalTitle>🌐 按标签学习URL</ModalTitle>
              <p style={{ color: 'var(--gray-600)', marginBottom: '1rem', fontSize: '0.875rem' }}>
                输入网址和标签，系统将只学习与标签相关的内容
              </p>
              
              <FormGroup>
                <Label>网址 *</Label>
                <Input
                  type="url"
                  value={urlLearnForm.url}
                  onChange={(e) => setUrlLearnForm({ ...urlLearnForm, url: e.target.value })}
                  placeholder="https://www.example.com/page"
                  disabled={urlLearning}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>标签 * (多个标签用逗号分隔)</Label>
                <Input
                  type="text"
                  value={urlLearnForm.tags}
                  onChange={(e) => setUrlLearnForm({ ...urlLearnForm, tags: e.target.value })}
                  placeholder="例如: 产品, 技术, 服务"
                  disabled={urlLearning}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                  只学习与这些标签相关的内容，不相关的会被过滤掉
                </p>
              </FormGroup>
              
              <FormGroup>
                <Label>分类 (可选)</Label>
                <Input
                  type="text"
                  value={urlLearnForm.category}
                  onChange={(e) => setUrlLearnForm({ ...urlLearnForm, category: e.target.value })}
                  placeholder="例如: 产品介绍"
                  disabled={urlLearning}
                />
              </FormGroup>
              
              {urlLearning && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                  <div style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</div>
                  <p style={{ marginTop: '0.5rem' }}>正在抓取并分析内容...</p>
                </div>
              )}
              
              {urlLearnResult && (
                <div style={{ 
                  marginTop: '1rem',
                  padding: '1rem', 
                  background: urlLearnResult.success ? '#f0fdf4' : urlLearnResult.skipped ? '#fef3c7' : '#fef2f2', 
                  borderRadius: '8px',
                  border: `1px solid ${urlLearnResult.success ? '#86efac' : urlLearnResult.skipped ? '#fcd34d' : '#fca5a5'}`
                }}>
                  {urlLearnResult.success ? (
                    <>
                      <div style={{ fontWeight: 600, color: '#166534', marginBottom: '0.5rem' }}>
                        ✅ 学习成功
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                        <div><strong>标题：</strong>{urlLearnResult.title}</div>
                        <div><strong>标签：</strong>{urlLearnResult.tags.join(', ')}</div>
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong>内容统计：</strong>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            原始 {urlLearnResult.originalLength} 字符 → 学习 {urlLearnResult.learnedLength} 字符 (保留率: {urlLearnResult.originalLength > 0 ? ((urlLearnResult.learnedLength / urlLearnResult.originalLength) * 100).toFixed(1) : 0}%)
                          </span>
                        </div>
                      </div>
                    </>
                  ) : urlLearnResult.skipped ? (
                    <>
                      <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '0.5rem' }}>
                        ⚠️ 内容已跳过
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                        <div>{urlLearnResult.message}</div>
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong>相关度：</strong>{urlLearnResult.relevanceScore}%
                        </div>
                        {urlLearnResult.originalLength && urlLearnResult.filteredLength && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            原始 {urlLearnResult.originalLength} 字符，过滤后 {urlLearnResult.filteredLength} 字符
                          </div>
                        )}
                        <p style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.5rem' }}>
                          系统只学习与指定标签相关的内容，相似度低于30%会被跳过
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: '0.5rem' }}>
                        ❌ 学习失败
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                        {urlLearnResult.message}
                      </div>
                    </>
                  )}
                </div>
              )}
              
              <ModalActions>
                <Button 
                  className="secondary" 
                  onClick={() => setShowUrlLearnModal(false)}
                  disabled={urlLearning}
                >
                  {urlLearnResult ? '关闭' : '取消'}
                </Button>
                {!urlLearnResult && (
                  <Button 
                    className="primary" 
                    onClick={handleUrlLearnSubmit}
                    disabled={urlLearning}
                  >
                    {urlLearning ? '学习中...' : '开始学习'}
                  </Button>
                )}
              </ModalActions>
            </Modal>
          </ModalOverlay>
        )}

        {showAutoLearnModal && (
          <ModalOverlay onClick={(e) => {
            if (e.target === e.currentTarget && !autoLearning) {
              setShowAutoLearnModal(false);
            }
          }}>
            <Modal style={{ width: '600px' }}>
              <ModalTitle>🔍 自动搜索学习</ModalTitle>

              <FormGroup>
                <Label>搜索关键词 <span style={{ color: '#e53e3e' }}>*</span></Label>
                <Input
                  type="text"
                  value={autoLearnForm.keywords}
                  onChange={(e) => setAutoLearnForm({ ...autoLearnForm, keywords: e.target.value })}
                  placeholder="例如: 航泰产品,智能客服,解决方案"
                  disabled={autoLearning}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  多个关键词用逗号分隔，系统会搜索与这些关键词相关的内容
                </p>
              </FormGroup>

              <FormGroup>
                <Label>限制条件 (不学习的关键词)</Label>
                <Input
                  type="text"
                  value={autoLearnForm.restrictions}
                  onChange={(e) => setAutoLearnForm({ ...autoLearnForm, restrictions: e.target.value })}
                  placeholder="例如: 竞争对手,广告,招聘"
                  disabled={autoLearning}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  包含这些关键词的内容不会被学习，多个用逗号分隔
                </p>
              </FormGroup>

              <FormGroup>
                <Label>最大结果数</Label>
                <Input
                  type="number"
                  value={autoLearnForm.maxResults}
                  onChange={(e) => setAutoLearnForm({ ...autoLearnForm, maxResults: e.target.value })}
                  placeholder="10"
                  min="1"
                  max="50"
                  disabled={autoLearning}
                />
              </FormGroup>

              <FormGroup>
                <Label>标签 (可选)</Label>
                <Input
                  type="text"
                  value={autoLearnForm.tags}
                  onChange={(e) => setAutoLearnForm({ ...autoLearnForm, tags: e.target.value })}
                  placeholder="例如: 产品,技术"
                  disabled={autoLearning}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  用于对学习内容的分类标记，多个用逗号分隔
                </p>
              </FormGroup>

              <FormGroup>
                <Label>分类 (可选)</Label>
                <Input
                  type="text"
                  value={autoLearnForm.category}
                  onChange={(e) => setAutoLearnForm({ ...autoLearnForm, category: e.target.value })}
                  placeholder="例如: 行业知识"
                  disabled={autoLearning}
                />
              </FormGroup>

              {autoLearning && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                  <div style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</div>
                  <p style={{ marginTop: '0.5rem' }}>正在搜索并学习内容，请稍候...</p>
                  <p style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                    这可能需要几分钟时间，请勿关闭页面
                  </p>
                </div>
              )}

              {autoLearnResult && (
                <div style={{ 
                  marginTop: '1rem',
                  padding: '1rem', 
                  background: autoLearnResult.success ? '#f0fdf4' : '#fef2f2', 
                  borderRadius: '8px',
                  border: `1px solid ${autoLearnResult.success ? '#86efac' : '#fca5a5'}`
                }}>
                  <div style={{ fontWeight: 600, color: autoLearnResult.success ? '#166534' : '#991b1b', marginBottom: '0.5rem' }}>
                    {autoLearnResult.success ? '✅ 自动学习完成' : '❌ 学习失败'}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                    <div>{autoLearnResult.message}</div>
                    {autoLearnResult.data && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                        <div>总计: {autoLearnResult.data.total}</div>
                        <div>成功学习: <span style={{ color: '#22c55e' }}>{autoLearnResult.data.learned}</span></div>
                        <div>跳过: <span style={{ color: '#f59e0b' }}>{autoLearnResult.data.skipped}</span></div>
                        <div>被限制: <span style={{ color: '#ef4444' }}>{autoLearnResult.data.blocked}</span></div>
                        <div>失败: <span style={{ color: '#6b7280' }}>{autoLearnResult.data.failed}</span></div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <ModalActions>
                <Button 
                  className="secondary" 
                  onClick={() => setShowAutoLearnModal(false)}
                  disabled={autoLearning}
                >
                  {autoLearnResult ? '关闭' : '取消'}
                </Button>
                {!autoLearnResult && (
                  <Button 
                    className="primary" 
                    onClick={handleAutoLearnSubmit}
                    disabled={autoLearning}
                  >
                    {autoLearning ? '学习中...' : '开始搜索学习'}
                  </Button>
                )}
              </ModalActions>
            </Modal>
          </ModalOverlay>
        )}
        
        {showGraphModal && (
          <ModalOverlay onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowGraphModal(false);
            }
          }}>
            <Modal style={{ width: '90%', maxWidth: '1200px', maxHeight: '90vh', overflow: 'auto' }}>
              <ModalTitle>🗺️ 知识图谱</ModalTitle>

              {graphLoading ? (
                <div style={{ padding: '3rem', textAlign: 'center' }}>
                  <div style={{ animation: 'spin 1s linear infinite', fontSize: '2rem' }}>⏳</div>
                  <p style={{ marginTop: '1rem', color: 'var(--gray-500)' }}>加载中...</p>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <button 
                      onClick={() => setActiveTab('overview')}
                      style={{ 
                        padding: '0.5rem 1rem', 
                        borderRadius: '4px',
                        border: activeTab === 'overview' ? '2px solid #10b981' : '1px solid #e5e7eb',
                        background: activeTab === 'overview' ? '#ecfdf5' : 'white',
                        cursor: 'pointer'
                      }}
                    >
                      📊 概览
                    </button>
                    <button 
                      onClick={() => setActiveTab('graph')}
                      style={{ 
                        padding: '0.5rem 1rem', 
                        borderRadius: '4px',
                        border: activeTab === 'graph' ? '2px solid #10b981' : '1px solid #e5e7eb',
                        background: activeTab === 'graph' ? '#ecfdf5' : 'white',
                        cursor: 'pointer'
                      }}
                    >
                      🔗 关系图谱
                    </button>
                    <button 
                      onClick={() => setActiveTab('categories')}
                      style={{ 
                        padding: '0.5rem 1rem', 
                        borderRadius: '4px',
                        border: activeTab === 'categories' ? '2px solid #10b981' : '1px solid #e5e7eb',
                        background: activeTab === 'categories' ? '#ecfdf5' : 'white',
                        cursor: 'pointer'
                      }}
                    >
                      📁 分类统计
                    </button>
                    <button 
                      onClick={() => setActiveTab('tags')}
                      style={{ 
                        padding: '0.5rem 1rem', 
                        borderRadius: '4px',
                        border: activeTab === 'tags' ? '2px solid #10b981' : '1px solid #e5e7eb',
                        background: activeTab === 'tags' ? '#ecfdf5' : 'white',
                        cursor: 'pointer'
                      }}
                    >
                      🏷️ 标签云
                    </button>
                  </div>

                  {activeTab === 'overview' && analysisData && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                      <div style={{ padding: '1rem', background: '#f0fdf4', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>{analysisData.totalItems}</div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>知识条目</div>
                      </div>
                      <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#d97706' }}>{analysisData.totalCategories}</div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>分类数量</div>
                      </div>
                      <div style={{ padding: '1rem', background: '#dbeafe', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2563eb' }}>{analysisData.totalTags}</div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>标签数量</div>
                      </div>
                      <div style={{ padding: '1rem', background: '#fce7f3', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#db2777' }}>{analysisData.totalQAPairs}</div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>问答对</div>
                      </div>
                      <div style={{ padding: '1rem', background: '#e0e7ff', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6366f1' }}>{analysisData.totalKeywords}</div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>关键词</div>
                      </div>
                      <div style={{ padding: '1rem', background: '#fecaca', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ef4444' }}>{(analysisData.totalContentLength / 1000).toFixed(1)}K</div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>总字符数</div>
                      </div>

                      <div style={{ gridColumn: '1 / -1', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                        <h4 style={{ marginBottom: '0.5rem', color: '#374151' }}>📅 最近学习的知识</h4>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          {analysisData.recentItems.map(item => (
                            <div key={item.id} style={{ padding: '0.5rem 1rem', background: 'white', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>{item.title}</div>
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                {new Date(item.learnedAt).toLocaleString('zh-CN')}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'graph' && graphData && (
                    <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px', minHeight: '400px' }}>
                      {graphData.totalEntities === 0 ? (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                          暂无数据，请先添加知识
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                          实体数: {graphData.totalEntities} | 关系数: {graphData.totalRelationships}
                        </div>
                      )}
                      <svg width="100%" height="400" style={{ border: '1px solid #e5e7eb', borderRadius: '4px', background: 'white' }}>
                        {graphData.entities?.slice(0, 20).map((entity, i) => {
                          const x = 100 + (i % 4) * 200;
                          const y = 80 + Math.floor(i / 4) * 100;
                          const colors = {
                            knowledge: '#10b981',
                            tag: '#f59e0b',
                            category: '#3b82f6',
                            keyword: '#ec4899'
                          };
                          return (
                            <g key={entity.id}>
                              <circle cx={x} cy={y} r="20" fill={colors[entity.type] || '#6b7280'} opacity="0.8" />
                              <text x={x} y={y + 5} textAnchor="middle" fill="white" fontSize="12" fontWeight="500">
                                {entity.name.charAt(0)}
                              </text>
                              <text x={x} y={y + 40} textAnchor="middle" fill="#374151" fontSize="11">
                                {entity.name.length > 8 ? entity.name.substring(0, 8) + '...' : entity.name}
                              </text>
                              <text x={x} y={y + 55} textAnchor="middle" fill="#9ca3af" fontSize="9">
                                {entity.type === 'knowledge' ? '知识' : entity.type === 'tag' ? '标签' : entity.type === 'category' ? '分类' : '关键词'}
                              </text>
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  )}

                  {activeTab === 'categories' && analysisData && (
                    <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {Object.entries(analysisData.byCategory).map(([category, count]) => (
                          <div key={category} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <div style={{ width: '150px', fontSize: '0.875rem', fontWeight: '500' }}>{category}</div>
                            <div style={{ flex: 1, background: '#e5e7eb', height: '20px', borderRadius: '10px', overflow: 'hidden' }}>
                              <div 
                                style={{ 
                                  height: '100%', 
                                  background: '#10b981', 
                                  width: `${(count / analysisData.totalItems) * 100}%`,
                                  transition: 'width 0.3s'
                                }} 
                              />
                            </div>
                            <div style={{ width: '50px', textAlign: 'right', fontSize: '0.875rem', color: '#6b7280' }}>{count}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'tags' && analysisData && (
                    <div style={{ padding: '2rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
                      {Object.entries(analysisData.byTag).map(([tag, count]) => {
                        const maxCount = Math.max(...Object.values(analysisData.byTag));
                        const size = 14 + (count / maxCount) * 20;
                        const opacity = 0.5 + (count / maxCount) * 0.5;
                        return (
                          <span 
                            key={tag}
                            style={{ 
                              padding: '0.5rem 1rem', 
                              background: '#dbeafe',
                              color: '#2563eb',
                              borderRadius: '20px',
                              fontSize: size,
                              opacity: opacity,
                              cursor: 'pointer',
                              transition: 'transform 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                          >
                            {tag} ({count})
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <ModalActions>
                <Button className="primary" onClick={() => setShowGraphModal(false)}>
                  关闭
                </Button>
              </ModalActions>
            </Modal>
          </ModalOverlay>
        )}

        {showMaintenanceModal && (
          <ModalOverlay onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMaintenanceModal(false);
            }
          }}>
            <Modal style={{ width: '500px' }}>
              <ModalTitle>⚙️ 知识库维护</ModalTitle>
              <ModalContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '0.5rem', color: '#92400e' }}>🗑️ 清理旧知识</h4>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                      清理30天前添加的旧知识，释放存储空间。
                    </p>
                    <button 
                      onClick={handleClearOldKnowledge}
                      style={{ 
                        padding: '0.5rem 1rem', 
                        background: '#f59e0b', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      清理旧知识
                    </button>
                  </div>

                  <div style={{ padding: '1rem', background: '#fee2e2', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '0.5rem', color: '#991b1b' }}>🗑️ 清空知识库</h4>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                      清空所有知识，此操作不可撤销！请谨慎操作。
                    </p>
                    <button 
                      onClick={handleClearAllKnowledge}
                      style={{ 
                        padding: '0.5rem 1rem', 
                        background: '#ef4444', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      清空所有知识
                    </button>
                  </div>

                  <div style={{ padding: '1rem', background: '#dbeafe', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '0.5rem', color: '#1d4ed8' }}>📥 导出知识库</h4>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                      将知识库导出为JSON文件，用于备份或迁移。
                    </p>
                    <button 
                      onClick={handleExportKnowledge}
                      style={{ 
                        padding: '0.5rem 1rem', 
                        background: '#3b82f6', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      导出知识库
                    </button>
                  </div>
                </div>
              </ModalContent>
              <ModalActions>
                <Button className="primary" onClick={() => setShowMaintenanceModal(false)}>
                  关闭
                </Button>
              </ModalActions>
            </Modal>
          </ModalOverlay>
        )}

        {toast && (
          <Toast className={toast.type}>
            {toast.message}
          </Toast>
        )}
      </Container>
    </AdminLayout>
  );
};

export default AdminKnowledge;